import useAxios from 'axios-hooks'
import React from 'react'
import { Home } from 'react-feather'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Card,
  CardBody,
  CustomInput,
  FormGroup,
  FormText,
  Label,
  Button,
  Spinner,
  Collapse,
} from 'reactstrap'

import Breadcrumb from '~/components/Breadcrumb'
import { Container } from './styles'
import styled from 'styled-components'

const StyledCustomInput = styled(CustomInput)`
  .custom-file-label::after {
    content: ${props => `"${props.buttonLabel}"`} !important;
  }
`

function BulkEditImport() {
  const intl = useIntl()
  const [file, setFile] = React.useState(null)
  const accountId = useSelector(state => state.user?.profile?.account?.id)

  const [{ loading }, patchLocations] = useAxios(
    {
      url: `/accounts/${accountId}/locations/csv`,
      method: 'PATCH',
    },
    { manual: true }
  )

  const handleSubmit = async () => {
    if (!file || loading) return

    const formData = new FormData()
    formData.append('file', file)

    await patchLocations({ data: formData })
      .then(() => {
        toast.success(
          intl.formatMessage({ id: 'Your locations are being updated!' })
        )
      })
      .catch(e => {
        const errors = e?.response?.data?.errors
        if (errors) {
          for (const [key, value] of Object.entries(errors)) {
            const firstError = value[0]

            const isMultiple = value.length > 1
            const rest = value.length - 1
            const multipleMessage = isMultiple
              ? intl.formatMessage(
                  {
                    id:
                      rest === 1
                        ? 'column_error_and'
                        : 'column_error_and_plural',
                  },
                  [rest]
                )
              : ''
            const columnName = intl.formatMessage({
              id: `${firstError?.column}_column`,
            })

            toast.error(
              intl.formatMessage({ id: key }, [columnName, firstError?.line]) +
                multipleMessage,
              {
                autoClose: false,
              }
            )
          }
        } else {
          if (e?.message === 'Network Error') {
            toast.error(
              intl.formatMessage({
                id:
                  'An error ocurred while trying to upload your file. Please try again.',
              }),
              {
                autoClose: false,
              }
            )
          } else {
            toast.error(
              intl.formatMessage({
                id:
                  'An error ocurred while trying to update your locations. Please try again.',
              }),
              {
                autoClose: false,
              }
            )
          }
        }
      })
  }

  return (
    <Container>
      <Breadcrumb
        paths={[
          {
            render: () => (
              <Link className="d-flex align-items-center" to="/">
                <Home size={20} />
              </Link>
            ),
          },
          {
            render: () => (
              <strong className="text-primary font-medium-3">
                <FormattedMessage id="Informations" />
              </strong>
            ),
          },
          {
            render: () => (
              <span className="font-medium-3">
                <FormattedMessage id="Import CSV" />
              </span>
            ),
          },
        ]}
      />

      <Card className="mb-1">
        <CardBody className="p-3">
          <h1 className="text-bold-400">
            <FormattedMessage id="Import CSV" />
          </h1>
          <form id="csv-form"></form>

          <FormGroup id="csv-form" className="px-0 col-12 col-sm-6 mt-1">
            <Label>
              <FormattedMessage id="Add a csv file" />
              *:
            </Label>
            <StyledCustomInput
              form="csv-form"
              id="file-uploader-csv"
              type="file"
              accept=".csv"
              onChange={e => setFile(e.target.files?.[0])}
              label={file?.name || intl.formatMessage({ id: 'Choose a file' })}
              buttonLabel={intl.formatMessage({ id: 'Browse' })}
            />
            <FormText>
              <a href="/modelo-csv.csv" download>
                <FormattedMessage id="Download the CSV model" />
              </a>
              . <FormattedMessage id="Upload only .csv files" />.
            </FormText>
          </FormGroup>

          <Button
            className="text-uppercase d-flex align-items-center"
            style={{ gap: 10 }}
            color="primary"
            disabled={!file || loading}
            onClick={handleSubmit}
          >
            <Collapse isOpen={loading} dir="">
              <Spinner size="sm" />
            </Collapse>
            <FormattedMessage id="Update Locations" />
          </Button>
        </CardBody>
      </Card>
    </Container>
  )
}

export default BulkEditImport
